import { useState, useEffect, useCallback } from "react";
import { getPageContentBySlug } from "@/actions";
import {
  parseText,
  parseJsonOrArray,
  getImageUrl,
  isVideoUrl,
} from "../our-technology/our-technology.hook";
import { ourLaboratoryImg } from "@/assets/images";

export const useAboutUs = (initialPageData = null, slug = "about-us") => {
  const [data, setData] = useState(initialPageData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [livePreviewData, setLivePreviewData] = useState(null);

  const fetchPageContent = useCallback(
    async (targetSlug = slug) => {
      setLoading(true);
      setError(null);
      try {
        const res = await getPageContentBySlug(targetSlug);
        if (res?.success && res?.data) {
          setData(res.data);
          return res.data;
        }
      } catch (err) {
        console.error(`Error fetching about-us page content for (${targetSlug}):`, err);
        setError(err?.message || "Failed to fetch about-us page content");
      } finally {
        setLoading(false);
      }
    },
    [slug]
  );

  useEffect(() => {
    if (!initialPageData) {
      fetchPageContent(slug);
    } else {
      setData(initialPageData);
    }
  }, [initialPageData, slug, fetchPageContent]);

  useEffect(() => {
    const handlePreviewMessage = (event) => {
      if (event.data?.type === "SUSTANIX_PREVIEW_DATA") {
        setLivePreviewData(event.data.data);
      }
    };
    window.addEventListener("message", handlePreviewMessage);
    return () => window.removeEventListener("message", handlePreviewMessage);
  }, []);

  const pageContent = data?.page?.page_content || {};
  const imageUrlBase = data?.imageUrl || "";

  // Hero section
  const heroPhotoRaw =
    livePreviewData?.heroCoverPhoto ||
    livePreviewData?.bgImage ||
    pageContent.heroCoverPhoto ||
    pageContent.cover_photo ||
    pageContent.heroPhoto ||
    pageContent.image;
  const heroPhoto = getImageUrl(heroPhotoRaw, imageUrlBase);

  // Section 2: Research mission
  const rawSec2Cards =
    parseJsonOrArray(livePreviewData?.sec2Cards) ||
    parseJsonOrArray(pageContent.sec2Cards || pageContent.sec2_cards || pageContent.section2Cards || pageContent.section2_cards);
  const formattedSec2Cards = Array.isArray(rawSec2Cards)
    ? rawSec2Cards.map((item, idx) => ({
        ...item,
        id: item.id || idx + 1,
        title: parseText(item.title || item.subTitle || item.name),
        description: parseText(item.description || item.desc || item.title),
        icon: getImageUrl(item.icon || item.image || item.photo, imageUrlBase) || item.icon,
      }))
    : null;

  // Section 3: Our approach
  const rawSec3Cards =
    parseJsonOrArray(livePreviewData?.sec3Cards) ||
    parseJsonOrArray(pageContent.sec3Cards || pageContent.sec3_cards || pageContent.section3Cards || pageContent.section3_cards);
  const formattedSec3Cards = Array.isArray(rawSec3Cards)
    ? rawSec3Cards.map((item, idx) => ({
        ...item,
        id: item.id || idx + 1,
        title: parseText(item.title || item.subTitle || item.name),
        subTitle: parseText(item.subTitle || item.title),
        description: parseText(item.description || item.desc),
        desc: parseText(item.desc || item.description),
        icon: getImageUrl(item.icon || item.image || item.photo, imageUrlBase) || item.icon,
      }))
    : null;

  // Section 4: Our process / strength
  const defaultProcessBgs = ["18, 178, 98", "45, 152, 218", "255, 178, 26"];
  const rawSec4Cards =
    parseJsonOrArray(livePreviewData?.sec4Cards) ||
    parseJsonOrArray(pageContent.sec4Cards || pageContent.sec4_cards || pageContent.section4Cards || pageContent.section4_cards);
  const formattedSec4Cards = Array.isArray(rawSec4Cards)
    ? rawSec4Cards.map((item, idx) => ({
        ...item,
        id: item.id || idx + 1,
        title: parseText(item.title || item.subTitle || item.name),
        subTitle: parseText(item.subTitle || item.title),
        description: parseText(item.description || item.desc),
        bg: item.bg || defaultProcessBgs[idx % defaultProcessBgs.length],
        icon: getImageUrl(item.icon || item.image || item.photo, imageUrlBase) || item.icon,
      }))
    : null;

  // Section 5: Our laboratory / capabilities
  const sec5PhotoRaw =
    livePreviewData?.sec5Photo ||
    livePreviewData?.sec5Image ||
    pageContent.sec5Photo ||
    pageContent.sec5_photo ||
    pageContent.sec5Image ||
    pageContent.sec5_image;
  const sec5Photo = getImageUrl(sec5PhotoRaw, imageUrlBase) || ourLaboratoryImg;

  const rawSec5Tags =
    parseJsonOrArray(livePreviewData?.sec5Tags) ||
    parseJsonOrArray(pageContent.sec5Tags || pageContent.sec5_tags);
  const formattedSec5Tags = Array.isArray(rawSec5Tags)
    ? rawSec5Tags.map((tag, idx) => {
        if (typeof tag === "string") return { id: idx + 1, name: tag };
        return {
          id: tag.id || idx + 1,
          name: parseText(tag.name || tag.label || tag.text || tag),
        };
      })
    : null;

  const rawSec5Stats =
    parseJsonOrArray(livePreviewData?.sec5Stats) ||
    parseJsonOrArray(pageContent.sec5Stats || pageContent.sec5_stats);
  const formattedSec5Stats = Array.isArray(rawSec5Stats)
    ? rawSec5Stats.map((stat, idx) => ({
        id: stat.id || idx + 1,
        number: parseText(stat.number || stat.value || stat.count || stat.title),
        label: parseText(stat.label || stat.description || stat.subTitle || stat.text),
      }))
    : null;

  // Section 6: Our team
  const rawSec6Members =
    parseJsonOrArray(livePreviewData?.sec6Members) ||
    parseJsonOrArray(pageContent.sec6Members || pageContent.sec6_members || pageContent.teamMembers || pageContent.team);
  const formattedSec6Members = Array.isArray(rawSec6Members)
    ? rawSec6Members.map((member, idx) => ({
        ...member,
        id: member.id || idx + 1,
        name: parseText(member.name),
        role: parseText(member.role || member.designation),
        designation: parseText(member.designation || member.role),
        image: getImageUrl(member.photo || member.image || member.img, imageUrlBase),
        photo: getImageUrl(member.photo || member.image || member.img, imageUrlBase),
        linkedin: member.linkedin,
        socials: member.socials,
      }))
    : null;

  // Section 7: Our specialization
  const rawSec7Cards =
    parseJsonOrArray(livePreviewData?.sec7Cards) ||
    parseJsonOrArray(pageContent.sec7Cards || pageContent.sec7_cards || pageContent.section7Cards || pageContent.section7_cards);
  const formattedSec7Cards = Array.isArray(rawSec7Cards)
    ? rawSec7Cards.map((item, idx) => ({
        ...item,
        id: item.id || idx + 1,
        title: parseText(item.title || item.subTitle || item.name),
        subTitle: parseText(item.subTitle || item.title),
        description: parseText(item.description || item.desc),
        type: item.type,
        imagePreview: getImageUrl(item.imagePreview || item.image || item.photo, imageUrlBase),
        image: getImageUrl(item.image || item.imagePreview || item.photo, imageUrlBase),
        alt: item.alt,
      }))
    : null;

  const activeContent = {
    // Hero
    heroTitle:
      livePreviewData?.heroTitle ||
      livePreviewData?.title ||
      parseText(pageContent.heroTitle || pageContent.title) ||
      "About us",
    heroDescription:
      livePreviewData?.heroDescription ||
      livePreviewData?.description ||
      parseText(pageContent.heroDescription || pageContent.description) ||
      "We are building the next generation of green-materials that help industries move beyond fossil-based plastics-without compromising performance, scale, or economics.",
    heroCoverPhoto: heroPhoto,
    heroIsVideo: isVideoUrl(
      heroPhotoRaw,
      livePreviewData?.heroIsVideo ?? (pageContent.heroIsVideo || pageContent.is_video || false)
    ),

    // Section 2: Research mission
    sec2Title:
      livePreviewData?.sec2Title ||
      parseText(pageContent.sec2Title || pageContent.sec2_title || pageContent.section2Title || pageContent.section2_title),
    sec2Description:
      livePreviewData?.sec2Description ||
      parseText(pageContent.sec2Description || pageContent.sec2_description || pageContent.section2Description || pageContent.section2_description),
    sec2DrawerTitle:
      livePreviewData?.sec2DrawerTitle ||
      parseText(pageContent.sec2DrawerTitle || pageContent.sec2_drawer_title || pageContent.section2DrawerTitle || pageContent.section2_drawer_title),
    sec2DrawerDescription:
      livePreviewData?.sec2DrawerDescription ||
      parseText(pageContent.sec2DrawerDescription || pageContent.sec2_drawer_description || pageContent.section2DrawerDescription || pageContent.section2_drawer_description),
    sec2CallActions:
      parseJsonOrArray(livePreviewData?.sec2CallActions || livePreviewData?.section2CallActions) ||
      parseJsonOrArray(pageContent.sec2CallActions || pageContent.sec2_call_actions || pageContent.section2CallActions || pageContent.section2_call_actions),
    sec2Cards: formattedSec2Cards,

    // Section 3: Our approach
    sec3Title:
      livePreviewData?.sec3Title ||
      parseText(pageContent.sec3Title || pageContent.sec3_title || pageContent.section3Title || pageContent.section3_title),
    sec3Cards: formattedSec3Cards,

    // Section 4: Our process / strength
    sec4Title:
      livePreviewData?.sec4Title ||
      parseText(pageContent.sec4Title || pageContent.sec4_title || pageContent.section4Title || pageContent.section4_title),
    sec4Description:
      livePreviewData?.sec4Description ||
      parseText(pageContent.sec4Description || pageContent.sec4_description || pageContent.section4Description || pageContent.section4_description),
    sec4Cards: formattedSec4Cards,

    // Section 5: Our laboratory / capabilities
    sec5Title:
      livePreviewData?.sec5Title ||
      parseText(pageContent.sec5Title || pageContent.sec5_title || pageContent.section5Title || pageContent.section5_title),
    sec5Description:
      livePreviewData?.sec5Description ||
      parseText(pageContent.sec5Description || pageContent.sec5_description || pageContent.section5Description || pageContent.section5_description),
    sec5ButtonText:
      livePreviewData?.sec5ButtonText ||
      parseText(pageContent.sec5ButtonText || pageContent.sec5_button_text),
    sec5ButtonLink:
      livePreviewData?.sec5ButtonLink ||
      pageContent.sec5ButtonLink ||
      pageContent.sec5_button_link,
    sec5InfoDescription:
      livePreviewData?.sec5InfoDescription ||
      parseText(pageContent.sec5InfoDescription || pageContent.sec5_info_description),
    sec5Tags: formattedSec5Tags,
    sec5Stats: formattedSec5Stats,
    sec5Photo: sec5Photo,

    // Section 6: Our team
    sec6Title:
      livePreviewData?.sec6Title ||
      parseText(pageContent.sec6Title || pageContent.sec6_title || pageContent.section6Title || pageContent.section6_title),
    sec6Members: formattedSec6Members,

    // Section 7: Our specialization
    sec7Title:
      livePreviewData?.sec7Title ||
      parseText(pageContent.sec7Title || pageContent.sec7_title || pageContent.section7Title || pageContent.section7_title),
    sec7Description:
      livePreviewData?.sec7Description ||
      parseText(pageContent.sec7Description || pageContent.sec7_description || pageContent.section7Description || pageContent.section7_description),
    sec7Cards: formattedSec7Cards,
  };

  return {
    data,
    setData,
    loading,
    error,
    livePreviewData,
    activeContent,
    imageUrlBase,
    fetchPageContent,
    getImageUrl: (src) => getImageUrl(src, imageUrlBase),
    isVideoUrl,
    parseText,
    parseJsonOrArray,
  };
};

export default useAboutUs;
